import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  EmojiEvents as ContestIcon,
  Groups as GroupsIcon,
  Leaderboard as LeaderboardIcon,
  Payments as PaymentsIcon,
  EmojiEvents as PrizeIcon,
  SmartDisplay as SmartDisplayIcon,
  Handshake as HandshakeIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import {
  CONTEST_PARTICIPANT_GROUPS,
  CONTEST_PRIZE_TEXT,
  CONTEST_REGISTRATION_TEXT,
} from '@utils/contest';

export interface ContestVideoItem {
  title?: string;
  url?: string;
  name?: string;
  notes?: string;
}

export interface ContestTargetItem {
  type?: string;
  code?: string;
  name?: string;
  sequence?: number;
}

export interface ContestTaskSponsor {
  id: string;
  name: string;
  logoUrl?: string;
  websiteUrl?: string;
  tier?: string;
  displayLabel?: string;
  displayOrder?: number;
}

export interface ContestTaskDisplay {
  id: string;
  title: string;
  description?: string;
  instructions?: string;
  targetItems?: ContestTargetItem[];
  participationFormat?: string;
  regularTrainingVideos?: ContestVideoItem[];
  creatorReferenceVideos?: ContestVideoItem[];
  amateurSampleVideos?: ContestVideoItem[];
  performanceSubmissionEnabled?: boolean;
  scoringNotes?: string;
  sponsors?: ContestTaskSponsor[];
  entry_fee?: number;
  currency?: string;
}

export const ContestVideoGroup: React.FC<{ title: string; videos?: ContestVideoItem[] }> = ({ title, videos }) => {
  const activeVideos = (videos || []).filter((video) => video?.url);
  if (activeVideos.length === 0) return null;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f4c81', mb: 0.75 }}>
        {title}
      </Typography>
      <Grid container spacing={1}>
        {activeVideos.map((video, index) => (
          <Grid item xs={12} md={6} key={`${title}-${video.url}-${index}`}>
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 1, bgcolor: '#ffffff' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {video.title || `Video ${index + 1}`}
              </Typography>
              {(video.name || video.notes) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {[video.name, video.notes].filter(Boolean).join(' | ')}
                </Typography>
              )}
              <Button
                size="small"
                startIcon={<SmartDisplayIcon />}
                onClick={() => window.open(video.url, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none', px: 0, mt: 0.5 }}
              >
                Watch
              </Button>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const ContestMetaPanel: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <Grid container spacing={1}>
    <Grid item xs={12} md={compact ? 12 : 4}>
      <Box sx={{ border: '1px solid #fed7aa', borderRadius: 2, p: 1.25, bgcolor: '#fff7ed', height: '100%' }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
          <GroupsIcon sx={{ color: '#c2410c', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#9a3412' }}>
            Open for All Ages
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Contests are open to everyone. Filter by skills, drills, fitness, fun format, or creative format.
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} md={compact ? 12 : 4}>
      <Box sx={{ border: '1px solid #bfdbfe', borderRadius: 2, p: 1.25, bgcolor: '#eff6ff', height: '100%' }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
          <LeaderboardIcon sx={{ color: '#1d4ed8', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1e40af' }}>
            Who Can Participate
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {CONTEST_PARTICIPANT_GROUPS.join(', ')}. Open for everyone across all contest formats.
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} md={compact ? 12 : 4}>
      <Box sx={{ border: '1px solid #bbf7d0', borderRadius: 2, p: 1.25, bgcolor: '#f0fdf4', height: '100%' }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
          <PrizeIcon sx={{ color: '#15803d', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#166534' }}>
            Prize Money & Fee
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 800, color: '#14532d' }}>
          {CONTEST_PRIZE_TEXT}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
          <PaymentsIcon sx={{ fontSize: 18, color: '#166534' }} />
          <Typography variant="body2" color="text.secondary">{CONTEST_REGISTRATION_TEXT}</Typography>
        </Stack>
      </Box>
    </Grid>
  </Grid>
);

interface ContestHubHeaderProps {
  sportName: string;
}

export const ContestHubHeader: React.FC<ContestHubHeaderProps> = ({ sportName }) => (
  <Box sx={{ mb: 3, p: { xs: 2, md: 2.5 }, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #dbe7f3' }}>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.75, color: '#0f4c81' }}>
          {sportName} Contest Hub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Explore contests by format, check results and leaderboard, then submit one performance video per challenge.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label="Open for Everyone" sx={{ bgcolor: '#f97316', color: '#fff', fontWeight: 900 }} />
        <Chip label={CONTEST_REGISTRATION_TEXT} sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 800 }} />
        <Chip label="Prize: 1st INR 5,000 | 2nd INR 2,000" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 800 }} />
      </Stack>
    </Stack>
  </Box>
);

export const ContestSponsorLogoStrip: React.FC<{ sponsors?: ContestTaskSponsor[] }> = ({ sponsors }) => {
  const list = (sponsors || []).filter((s) => s?.name);
  if (!list.length) return null;

  const primary = list[0];
  const label = primary.displayLabel || 'Presented by';

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        background: 'linear-gradient(90deg, #fef3c7 0%, #fff7ed 55%, #ffffff 100%)',
        border: '1px solid #fde68a',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      {list.map((sponsor) => (
        <Stack key={sponsor.id} direction="row" spacing={1} alignItems="center">
          {sponsor.logoUrl ? (
            <Box
              component="img"
              src={sponsor.logoUrl}
              alt={sponsor.name}
              sx={{ height: 28, maxWidth: 100, objectFit: 'contain' }}
            />
          ) : null}
          {sponsor.websiteUrl ? (
            <Box
              component="a"
              href={sponsor.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 800, color: '#0f4c81', textDecoration: 'none', fontSize: '0.85rem' }}
            >
              {sponsor.name}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f4c81' }}>
              {sponsor.name}
            </Typography>
          )}
          {sponsor.tier && sponsor.tier !== 'supporting' && (
            <Chip label={sponsor.tier} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#fff', color: '#b45309' }} />
          )}
        </Stack>
      ))}
    </Box>
  );
};

interface ContestTaskCardProps {
  task: ContestTaskDisplay;
  highlighted?: boolean;
  uploadSection?: React.ReactNode;
  onViewResults?: () => void;
  onViewLeaderboard?: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
}

export const ContestTaskCard: React.FC<ContestTaskCardProps> = ({
  task,
  highlighted = false,
  uploadSection,
  onViewResults,
  onViewLeaderboard,
  onSubmit,
  submitLabel = 'Submit Performance',
}) => (
  <Card
    sx={{
      border: highlighted ? '2px solid #c2410c' : '1px solid #dbe7f3',
      boxShadow: highlighted ? '0 12px 28px rgba(194, 65, 12, 0.18)' : '0 8px 24px rgba(15, 76, 129, 0.08)',
      borderRadius: 3,
    }}
  >
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Chip size="small" label="Open for Everyone" sx={{ bgcolor: '#f97316', color: '#fff', fontWeight: 900 }} />
            <Chip size="small" label={CONTEST_REGISTRATION_TEXT} sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 800 }} />
            <Chip size="small" label="Results & Leaderboard" sx={{ bgcolor: '#ecfccb', color: '#3f6212', fontWeight: 800 }} />
          </Stack>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f4c81' }}>
            {task.title}
          </Typography>
          <ContestSponsorLogoStrip sponsors={task.sponsors} />
          {task.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {task.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {(task.targetItems || []).map((item, index) => (
              <Chip
                key={`${item.type}-${item.code}-${index}`}
                size="small"
                label={`${item.type || 'item'}: ${item.name || item.code || 'Mapped item'}`}
                sx={{ bgcolor: '#eef6ff', color: '#0f4c81', fontWeight: 700 }}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.25, border: '1px solid #e2e8f0' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Format
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {task.participationFormat || 'Open participation'}
            </Typography>
            {task.scoringNotes && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Scoring: {task.scoringNotes}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 1.5 }}>
        <ContestMetaPanel />
      </Box>

      {task.instructions && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {task.instructions}
        </Alert>
      )}

      <ContestVideoGroup title="Regular Training Videos" videos={task.regularTrainingVideos} />
      <ContestVideoGroup title="Creator / Reference Videos" videos={task.creatorReferenceVideos} />
      <ContestVideoGroup title="Amateur Videos" videos={task.amateurSampleVideos} />

      {uploadSection}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        {onViewResults && (
          <Button
            variant="outlined"
            startIcon={<LeaderboardIcon />}
            onClick={onViewResults}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            View Results
          </Button>
        )}
        {onViewLeaderboard && (
          <Button
            variant="outlined"
            startIcon={<ContestIcon />}
            onClick={onViewLeaderboard}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Leaderboard
          </Button>
        )}
        {onSubmit && (
          <Button variant="contained" onClick={onSubmit} sx={{ textTransform: 'none', fontWeight: 900 }}>
            {submitLabel}
          </Button>
        )}
      </Stack>
    </CardContent>
  </Card>
);

interface FeaturedSmashContestProps {
  sportName: string;
  onViewResults?: () => void;
  onViewLeaderboard?: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
}

export const FeaturedSmashContest: React.FC<FeaturedSmashContestProps> = ({
  sportName,
  onViewResults,
  onViewLeaderboard,
  onSubmit,
  submitLabel = 'Submit Your Video',
}) => {
  if (sportName.toLowerCase() !== 'badminton') return null;

  return (
    <Card
      sx={{
        mb: 3,
        overflow: 'hidden',
        border: '1px solid rgba(30, 64, 175, 0.18)',
        borderRadius: 4,
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)',
        background: 'linear-gradient(135deg, #06162f 0%, #102a57 48%, #f97316 130%)',
        color: '#fff',
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 12% 18%, rgba(255,255,255,0.18), transparent 28%), radial-gradient(circle at 88% 12%, rgba(251,146,60,0.28), transparent 26%)',
          pointerEvents: 'none',
        },
      }}
    >
      <CardContent sx={{ position: 'relative', p: { xs: 2, md: 3 } }}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: '100%',
                minHeight: 360,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.22)',
                bgcolor: 'rgba(255,255,255,0.08)',
                display: 'grid',
                gridTemplateRows: '1fr 0.72fr',
                gap: 1,
                p: 1,
              }}
            >
              {['/images/Badminton-Smash-Contest.jpg', '/images/Badminton-Contest.jpg'].map((src, index) => (
                <Box
                  key={src}
                  component="img"
                  src={src}
                  alt={index === 0 ? 'Badminton smash contest poster' : 'Badminton contest poster'}
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 2,
                    filter: 'saturate(1.08) contrast(1.04)',
                  }}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Chip label="Open Contest" size="small" sx={{ bgcolor: '#f97316', color: '#fff', fontWeight: 900 }} />
                  <Chip label="Badminton" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', fontWeight: 800 }} />
                  <Chip label="Deadline: August 31" size="small" sx={{ bgcolor: 'rgba(34,197,94,0.22)', color: '#dcfce7', fontWeight: 800 }} />
                  <Chip label="Registration: INR 500" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', fontWeight: 800 }} />
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1.02, letterSpacing: '-0.04em' }}>
                  Maximum Smashes per Minute
                </Typography>
                <Typography variant="h6" sx={{ color: '#fde68a', fontWeight: 800, mt: 1 }}>
                  Power, footwork, recovery, and technical consistency under fatigue.
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                {[
                  { label: 'Duration', value: '60 seconds' },
                  { label: 'Feed Pattern', value: 'Deep left and right backcourt' },
                  { label: 'Recovery Rule', value: 'Return fully to center marker' },
                  { label: 'Scoring Priority', value: 'Technical smash quality before volume' },
                  { label: 'Prize Money', value: '1st INR 5,000 | 2nd INR 2,000' },
                  { label: 'Format', value: 'Skills contest open for all ages' },
                ].map((item) => (
                  <Box key={item.label} sx={{ border: '1px solid rgba(255,255,255,0.16)', borderRadius: 2, p: 1.25, bgcolor: 'rgba(255,255,255,0.08)' }}>
                    <Typography variant="caption" sx={{ color: '#bfdbfe', fontWeight: 800, textTransform: 'uppercase' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{item.value}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 1.5, border: '1px solid rgba(255,255,255,0.16)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#fff', mb: 0.75 }}>
                  Submission Requirements
                </Typography>
                <Stack spacing={0.75}>
                  {[
                    'Stable tripod video showing full body, court boundaries, recovery marker, and shuttle path.',
                    'Overlay total valid technical smashes plus heart rate or calorie data if available.',
                    'Mention racket/string tension, footwear, and tracker or wearable model used.',
                    'Submitter may record at clubs, academies, schools, corporate zones, or safe practice spaces.',
                  ].map((text) => (
                    <Typography key={text} variant="body2" sx={{ color: '#e5edff' }}>
                      {text}
                    </Typography>
                  ))}
                </Stack>
              </Box>

              <Box sx={{ bgcolor: 'rgba(255,255,255,0.94)', borderRadius: 2, p: 1.25 }}>
                <ContestMetaPanel />
              </Box>

              <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 'auto' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#bfdbfe', fontWeight: 800, textTransform: 'uppercase' }}>
                    Timeline
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#fff', fontWeight: 800 }}>
                    Submissions open now. Final deadline: August 31.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {onViewResults && (
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<LeaderboardIcon />}
                      onClick={onViewResults}
                      sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
                    >
                      Results
                    </Button>
                  )}
                  {onViewLeaderboard && (
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<ContestIcon />}
                      onClick={onViewLeaderboard}
                      sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
                    >
                      Leaderboard
                    </Button>
                  )}
                  {onSubmit && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={onSubmit}
                      sx={{
                        bgcolor: '#f97316',
                        color: '#fff',
                        px: 3,
                        py: 1.1,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontWeight: 900,
                        boxShadow: '0 14px 28px rgba(249,115,22,0.28)',
                        '&:hover': { bgcolor: '#ea580c' },
                      }}
                    >
                      {submitLabel}
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

interface ContestSponsorBannerProps {
  sportName?: string;
}

export const ContestSponsorBanner: React.FC<ContestSponsorBannerProps> = ({ sportName }) => (
  <Box
    sx={{
      mb: 3,
      p: { xs: 2, md: 2.5 },
      borderRadius: 3,
      overflow: 'hidden',
      position: 'relative',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4c1d95 100%)',
      border: '1px solid rgba(167, 139, 250, 0.35)',
      boxShadow: '0 16px 40px rgba(76, 29, 149, 0.25)',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 90% 10%, rgba(251, 191, 36, 0.2) 0%, transparent 45%)',
        pointerEvents: 'none',
      }}
    />
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      justifyContent="space-between"
      sx={{ position: 'relative' }}
    >
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <HandshakeIcon sx={{ color: '#fbbf24' }} />
          <Chip
            label="Open for sponsors"
            size="small"
            sx={{ bgcolor: 'rgba(251, 191, 36, 0.2)', color: '#fde68a', fontWeight: 900 }}
          />
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 900, color: '#fff', mb: 0.75 }}>
          Partner with {sportName ? `${sportName} contests` : 'EncycGames contests'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#c4b5fd', maxWidth: 560 }}>
          Multi-tier sponsorship for contest hubs, training videos, and content. Packages matched to your budget and category — global brands welcome, India-first rollout.
        </Typography>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          component={RouterLink}
          to={sportName ? `/sponsors/opportunities?sport=${encodeURIComponent(sportName)}` : '/sponsors/opportunities'}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 900,
            bgcolor: '#f59e0b',
            color: '#1e1b4b',
            '&:hover': { bgcolor: '#fbbf24' },
          }}
        >
          View opportunities
        </Button>
        <Button
          component={RouterLink}
          to="/sponsors#express-interest"
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            color: '#e9d5ff',
            borderColor: 'rgba(233, 213, 255, 0.45)',
          }}
        >
          Express interest
        </Button>
      </Stack>
    </Stack>
  </Box>
);
